import { useMemo } from "react";
import { formatDistanceToNow, format } from "date-fns";
import {
  AlertCircle,
  AlarmClock,
  BadgeCheck,
  Bell,
  ClipboardCheck,
  Mail,
  Shield,
  Timer,
} from "lucide-react";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import type {
  DiscountApprovalPolicySummary,
  DiscountApprovalRequest,
  DiscountApprovalWorkflowSummary,
} from "@/lib/api/discountApprovals";
import type { OrderDiscountSummary } from "@/lib/api/orders";

const currencyFormatter = new Intl.NumberFormat(undefined, {
  style: "currency",
  currency: "USD",
});

const formatCurrency = (cents: number, currency?: string | null) => {
  if (!currency || currency.toUpperCase() === "USD") {
    return currencyFormatter.format(cents / 100);
  }
  return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(cents / 100);
};

const formatPercent = (bps: number | null) => {
  if (bps === null || Number.isNaN(bps)) {
    return "-";
  }
  return `${(bps / 100).toFixed(2)}%`;
};

const resolvePolicyDisplay = (
  policy: DiscountApprovalPolicySummary | null,
  discountSnapshot: OrderDiscountSummary | null | undefined,
) => {
  if (policy) {
    return {
      name: policy.name,
      code: policy.code,
      type: policy.discountType,
      valueLabel:
        policy.discountType === "percentage"
          ? formatPercent(policy.valueBps ?? null)
          : formatCurrency(policy.valueCents ?? 0, "USD"),
      allowStacking: policy.allowStacking,
      stackGroup: policy.stackGroup,
      maxStack: policy.maxStack,
      reasonCodes: policy.reasonCodes,
    };
  }

  if (discountSnapshot?.policySnapshot) {
    const snapshot = discountSnapshot.policySnapshot;
    const name =
      typeof snapshot.name === "string" && snapshot.name.length > 0 ? snapshot.name : "Custom policy";
    const code =
      typeof snapshot.code === "string" && snapshot.code.length > 0 ? snapshot.code : "-";
    const discountType =
      typeof snapshot.discountType === "string" && snapshot.discountType === "amount"
        ? "amount"
        : "percentage";
    const valueBps =
      typeof snapshot.valueBps === "number" && Number.isFinite(snapshot.valueBps)
        ? snapshot.valueBps
        : null;
    const valueCents =
      typeof snapshot.valueCents === "number" && Number.isFinite(snapshot.valueCents)
        ? snapshot.valueCents
        : null;
    return {
      name,
      code,
      type: discountType,
      valueLabel:
        discountType === "percentage"
          ? formatPercent(valueBps)
          : formatCurrency(valueCents ?? 0, "USD"),
      allowStacking:
        typeof snapshot.allowStacking === "boolean" ? snapshot.allowStacking : discountSnapshot?.requiresApproval ?? false,
      stackGroup:
        typeof snapshot.stackGroup === "string" && snapshot.stackGroup.length > 0
          ? snapshot.stackGroup
          : null,
      maxStack:
        typeof snapshot.maxStack === "number" && Number.isFinite(snapshot.maxStack)
          ? Math.trunc(snapshot.maxStack)
          : null,
      reasonCodes: [],
    };
  }

  return null;
};

type DiscountApprovalSummaryCardProps = {
  request: DiscountApprovalRequest;
  policy: DiscountApprovalPolicySummary | null;
  workflow: DiscountApprovalWorkflowSummary | null;
  orderDiscount?: OrderDiscountSummary | null;
};

export const DiscountApprovalSummaryCard = ({
  request,
  policy,
  workflow,
  orderDiscount,
}: DiscountApprovalSummaryCardProps) => {
  const policyDisplay = useMemo(() => resolvePolicyDisplay(policy, orderDiscount), [policy, orderDiscount]);

  const slaLabel = useMemo(() => {
    if (!request.expiresAt) {
      return null;
    }
    try {
      const expiresAtDate = new Date(request.expiresAt);
      return formatDistanceToNow(expiresAtDate, { addSuffix: true });
    } catch {
      return null;
    }
  }, [request.expiresAt]);

  const requestedAgo = useMemo(() => {
    try {
      return formatDistanceToNow(new Date(request.requestedAt), { addSuffix: true });
    } catch {
      return null;
    }
  }, [request.requestedAt]);

  const statusBadgeClass = useMemo(() => {
    switch (request.status) {
      case "approved":
        return "bg-emerald-500/10 text-emerald-600 border-emerald-500/30 dark:text-emerald-300";
      case "declined":
      case "cancelled":
        return "bg-rose-500/10 text-rose-600 border-rose-500/30 dark:text-rose-300";
      case "escalated":
        return "bg-amber-500/10 text-amber-600 border-amber-500/30 dark:text-amber-300";
      case "expired":
        return "bg-slate-500/10 text-slate-600 border-slate-500/30 dark:text-slate-300";
      case "in_review":
        return "bg-sky-500/10 text-sky-600 border-sky-500/30 dark:text-sky-300";
      default:
        return "bg-blue-500/10 text-blue-600 border-blue-500/30 dark:text-blue-300";
    }
  }, [request.status]);

  return (
    <Card className="border-border/60 shadow-sm">
      <CardHeader className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Shield className="h-5 w-5" />
          </span>
          <div>
            <CardTitle className="text-lg">Discount approval</CardTitle>
            <CardDescription>
              Workflow metadata and notifier preferences for this discount request.
            </CardDescription>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge className={cn("text-xs font-semibold uppercase tracking-wide", statusBadgeClass)}>
            {request.status.replaceAll("_", " ")}
          </Badge>
          {slaLabel ? (
            <Badge variant="outline" className="border-dashed text-xs">
              <AlarmClock className="mr-1 h-3 w-3" />
              {slaLabel}
            </Badge>
          ) : null}
        </div>
      </CardHeader>
      <CardContent className="grid gap-6 md:grid-cols-[1.4fr_1fr]">
        <div className="space-y-4">
          <div className="space-y-2 rounded-xl border border-border/70 bg-muted/30 p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Requested discount
                </p>
                <p className="text-xl font-semibold">
                  {formatCurrency(request.discountAmountCents, orderDiscount?.currency ?? "USD")}
                </p>
                {request.discountPercentBps ? (
                  <p className="text-xs text-muted-foreground">
                    Effective rate {formatPercent(request.discountPercentBps)}
                  </p>
                ) : null}
              </div>
              <div className="rounded-lg bg-background px-3 py-2 text-sm shadow-sm">
                <div className="flex items-center gap-1 text-muted-foreground">
                  <ClipboardCheck className="h-3.5 w-3.5" />
                  <span>Requested</span>
                </div>
                <p className="text-xs text-foreground">
                  {requestedAgo ?? format(new Date(request.requestedAt), "PP p")}
                </p>
              </div>
            </div>
            {request.reasonCode || request.reasonNote ? (
              <div className="rounded-lg bg-background/60 p-3">
                <p className="text-xs font-semibold uppercase text-muted-foreground">Reason</p>
                <div className="mt-1 space-y-1 text-sm">
                  {request.reasonCode ? (
                    <Badge variant="outline" className="bg-secondary/20 text-xs font-medium">
                      {request.reasonCode}
                    </Badge>
                  ) : null}
                  {request.reasonNote ? <p>{request.reasonNote}</p> : null}
                </div>
              </div>
            ) : null}
          </div>

          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
              <BadgeCheck className="h-4 w-4 text-primary" />
              Policy &amp; stacking
            </div>
            {policyDisplay ? (
              <div className="rounded-xl border border-border/60 bg-background/80 p-4">
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="font-medium text-foreground">{policyDisplay.name}</span>
                  <Badge variant="outline" className="text-xs">
                    {policyDisplay.code}
                  </Badge>
                  <Badge variant="secondary" className="text-xs uppercase">
                    {policyDisplay.type === "percentage" ? "Percentage" : "Amount"}
                  </Badge>
                  <Badge className="bg-primary/10 text-primary">{policyDisplay.valueLabel}</Badge>
                </div>
                <Separator className="my-3" />
                <div className="space-y-2 text-xs text-muted-foreground">
                  <p>
                    Stacking:{" "}
                    {policyDisplay.allowStacking
                      ? `Allowed${policyDisplay.stackGroup ? ` (${policyDisplay.stackGroup})` : ""}`
                      : "Not allowed"}
                  </p>
                  {policyDisplay.maxStack ? (
                    <p>Max stack count: {policyDisplay.maxStack}</p>
                  ) : null}
                  {policyDisplay.reasonCodes.length > 0 ? (
                    <div className="space-y-1">
                      <p>Reason codes:</p>
                      <div className="flex flex-wrap gap-1.5">
                        {policyDisplay.reasonCodes.map((reason) => (
                          <Badge key={reason.code} variant="outline" className="text-[11px]">
                            {reason.label}
                            {reason.requiresNote ? (
                              <span className="ml-1 rounded-full bg-amber-500/30 px-1 text-[10px] uppercase text-amber-600">
                                note
                              </span>
                            ) : null}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            ) : (
              <div className="rounded-xl border border-dashed border-muted-foreground/40 bg-muted/10 p-4 text-xs text-muted-foreground">
                No policy metadata available.
              </div>
            )}
          </div>
        </div>

        <div className="space-y-4 rounded-xl border border-border/60 bg-muted/20 p-4">
          <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
            <Bell className="h-4 w-4 text-primary" />
            Workflow &amp; notifications
          </div>
          {workflow ? (
            <div className="space-y-3 text-sm">
              <div className="rounded-lg bg-background/70 p-3">
                <p className="text-xs font-semibold uppercase text-muted-foreground">Threshold</p>
                <p className="text-sm font-medium text-foreground">
                  {formatCurrency(workflow.thresholdCents, "USD")}
                </p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase text-muted-foreground">Approver roles</p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {workflow.approverRoles.map((role) => (
                    <Badge key={role} variant="secondary" className="text-[11px] uppercase">
                      {role}
                    </Badge>
                  ))}
                </div>
              </div>
              {workflow.escalationEmails.length > 0 ? (
                <div>
                  <p className="text-xs font-semibold uppercase text-muted-foreground">
                    Escalation notifications
                  </p>
                  <div className="mt-2 space-y-1 text-xs">
                    {workflow.escalationEmails.map((email) => (
                      <div key={email} className="flex items-center gap-2 rounded-md bg-background/70 px-2 py-1.5">
                        <Mail className="h-3.5 w-3.5 text-muted-foreground" />
                        <span>{email}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2 rounded-md border border-dashed border-muted-foreground/40 px-2 py-2 text-xs text-muted-foreground">
                  <Mail className="h-3.5 w-3.5" />
                  <span>No escalation emails configured.</span>
                </div>
              )}
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <AlertCircle className="h-3.5 w-3.5" />
                {workflow.requireReasonCode
                  ? "Declines must include a reason note."
                  : "Decline notes optional for this workflow."}
              </div>
              {workflow.steps.length > 0 ? (
                <div className="rounded-lg bg-background/60 p-3">
                  <p className="text-xs font-semibold uppercase text-muted-foreground">Step sequence</p>
                  <div className="mt-2 space-y-2">
                    {workflow.steps.map((step) => (
                      <div
                        key={step.id}
                        className="flex items-start justify-between gap-3 rounded-md border border-border/60 px-2.5 py-2"
                      >
                        <div className="space-y-1">
                          <div className="flex items-center gap-1 text-sm font-medium">
                            <Timer className="h-3.5 w-3.5 text-primary" />
                            <span>
                              Step {step.stepOrder + 1}: {step.approverRole}
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Notify via {step.notifyChannel ?? "push notifications"}
                            {step.fallbackRole ? ` · Fallback ${step.fallbackRole}` : ""}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-muted-foreground/40 bg-muted/10 p-4 text-xs text-muted-foreground">
              Workflow metadata not available for this request.
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
